import { Icon } from "@iconify/react";
import { Breadcrumbs, Card, CardContent } from "@mui/material";
import { forwardRef, useEffect, useState } from "react";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import { pageConstant } from "../../../config/pageConstant";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import RefreshIcon from "@mui/icons-material/Refresh";
import excel from "../../../assets/images/excel.svg";
import axios from "axios";
import { serviceAPIMapping } from "../../../config/service-api-mapping";
import _ from "lodash";
import { Column } from "ag-grid-community";
import { constants, EndPoint, messages, VALID_RATINGS } from "../../../common/constants";
import RiskByCptyGrid from "../../../components/RiskByCptyGrid";
import { exportMultipleSheetsAsExcel } from "ag-grid-enterprise";
import { hasAccess, isValidGuid } from "../../../common/commonFunctions";
import moment from "moment";
import SpinnerFullScreen from "../../../components/SpinnerFullScreen";

const RiskByCpty = () => {
  const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref
  ) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<any>("");
  const [gridApi, setGridApi] = useState<any>(null);
  const [gridColumnApi, setGridColumnApi] = useState<any>(null);
  const [gridRef, setGridRef] = useState<any>(null); // child ref (gridRef.current)
  const [lastUpdatedDate, setLastUpdatedDate] = useState<string>(moment(new Date()).format(constants.RefreshFormat));
  const [doShowFullScreenLoader, setDoShowFullScreenLoader] =
    useState<boolean>(false);

  const token = window.localStorage.getItem("refreshtoken");

  const [rowData, setRowData] = useState<any[]>([]);
  const [hasError, setHasError] = useState<boolean>(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(true);
  const [isAddDisabled, setIsAddDisabled] = useState<boolean>(false);

  const getCardHeight = () => {
    const offsetHeight = document?.getElementById(
      "sec-master-breadcrumbs"
    )?.offsetHeight;
    const divHeight = _.isUndefined(offsetHeight) ? 22 : offsetHeight + 10;

    return "calc(100% - " + divHeight + "px)";
  };

  useEffect(() => {
    getContractsByRisk();
  }, []);

  useEffect(() => {
    if (gridApi && rowData.length > 0) {
      gridApi.sizeColumnsToFit();
    }
  }, [gridApi, rowData]);

  const getContractsByRisk = async () => {
    setIsSaveDisabled(true);
    setIsCancelDisabled(true);
    setIsAddDisabled(false);

    setDoShowFullScreenLoader(true);

    try {
      const raw = JSON.stringify({
        function_code: "fn_risk_get_cpty_list",
      });

      const response = await axios.post(
        `${serviceAPIMapping["REPORTING-SERVICE"].apiRouteURL}${EndPoint.reportFn}`,
        raw,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response) {
        setSnackbarMessage(messages.lemurServiceDown);
        return;
      }

      if (response?.status === 200) {
        if (response?.data && _.isArray(response?.data)) {
          const rows = response.data.map((row: any, index: number) => {
            return {
              ...row,
              probability_of_default_estimated:
                (_.isString(row?.probability_of_default_estimated) &&
                  row?.probability_of_default_estimated?.length === 0) ||
                (row?.probability_of_default_estimated &&
                  isNaN(row?.probability_of_default_estimated))
                  ? 0
                  : row?.probability_of_default_estimated,
              use_estimated: row?.use_estimated === true ? "Yes" : "No",
              inherit_parent_account_pd:
                row?.inherit_parent_account_pd === true ? "Yes" : "No",
              inherit_credit_account_pd:
                row?.inherit_credit_account_pd === true ? "Yes" : "No",
              rowId: index.toString(),
              cb_rating: row?.cb_rating?.toUpperCase(),
            };
          });

          setRowData(rows);
        }
      }
    } catch (error: any) {
      console.log("error", error);
      setRowData([]);
    } finally {
      setDoShowFullScreenLoader(false);
    }
  };

  const saveCPTYData = async (rows: any) => {
    try {
      setDoShowFullScreenLoader(true);
      const req = [...rows]?.map((row) => {
        return {
          tradeBlazerAccountNo: row?.trade_blazer_account_no,
          tradeBlazerAccountShortName: row?.trade_blazer_account_short_name,
          name: row?.name,
          bucket_cb: row?.bucket_cb,
          pd_data: row?.probability_of_default_cb,
          bucket_estimated: row?.bucket_estimated,
          pd_estimated: row?.pd_estimated,
          use_estimated: true,
          parent_account: null,
          credit_account: row?.credit_account,
          inherit_parent_account_pd:
            row?.inherit_parent_account_pd === "Yes" ||
            row?.inherit_parent_account_pd === "yes",
          inherit_credit_account_pd:
            row?.inherit_credit_account_pd === "Yes" ||
            row?.inherit_credit_account_pd === "yes",
        };
      });
      const raw = JSON.stringify(req?.[0]);

      const token = window.localStorage.getItem("refreshtoken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      await axios
        .post(
          `${serviceAPIMapping["LEMUR-SERVICE"].apiRouteURL}${EndPoint.saveRiskCpty}`,
          raw,
          {
            headers: headers,
          }
        )
        .then((response: any) => {
          if (!response) {
            setOpen(true);
            setSnackbarMessage(messages.SecMasterDownMessage);
          } else if (!_.isUndefined(response) && response?.status === 200) {
            // toast.success('Sec master data saved successfully')
            getContractsByRisk();
          } else if (response?.status === 401) {
            // logout(channel)
          } else {
          }
        });
    } catch (err) {
      // toast.success('Failed to save Sec master data')

      getContractsByRisk();
    } finally {
      setDoShowFullScreenLoader(false);
    }
  };

  const updateCPTYData = async (rows: any) => {
    try {
      setDoShowFullScreenLoader(true);
      const req = [...rows]?.map((row) => {
        return {
          tradeBlazerAccountNo: row?.trade_blazer_account_no,
          tradeBlazerAccountShortName: row?.trade_blazer_account_short_name,
          name: row?.name,
          bucket_cb: row?.bucket_cb,
          pd_data: row?.probability_of_default_cb,
          bucket_estimated: row?.bucket_estimated,
          pd_estimated: row?.pd_estimated,
          use_estimated: true,
          parent_account: null,
          credit_account: row?.credit_account,
          inherit_parent_account_pd:
            row?.inherit_parent_account_pd === "Yes" ||
            row?.inherit_parent_account_pd === "yes",
          inherit_credit_account_pd:
            row?.inherit_credit_account_pd === "Yes" ||
            row?.inherit_credit_account_pd === "yes",
          id: row?.id,
        };
      });
      const raw = JSON.stringify(req?.[0]);

      const token = window.localStorage.getItem("refreshtoken");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      await axios
        .put(
          `${serviceAPIMapping["LEMUR-SERVICE"].apiRouteURL}${EndPoint.saveRiskCpty}`,
          raw,
          {
            headers: headers,
          }
        )
        .then((response: any) => {
          if (!response) {
            setOpen(true);
            setSnackbarMessage(messages.serviceDownMessage);
          } else if (!_.isUndefined(response) && response?.status === 200) {
            getContractsByRisk();
          } else if (response?.status === 401) {
            // logout(channel)
          } else {
          }
        });
    } catch (err) {
      getContractsByRisk();
    } finally {
      setDoShowFullScreenLoader(false);
    }
  };

  const onSave = () => {
    const insertRows = [...rowData]?.filter((x: any) => x.isNewRowInsert);
    const updateRows = [...rowData]?.filter((x: any) => x.isRowUpdated);

    if (_.isArray(insertRows) && insertRows?.length > 0) {
      const validRows = insertRows?.filter((row: any) => isValidRateRow(row));

      if (_.isArray(validRows) && validRows?.length > 0) {
        saveCPTYData(insertRows);
      }
    }

    if (_.isArray(updateRows) && updateRows?.length > 0) {
      const validRows = updateRows?.filter((row: any) => isValidRateRow(row));
      if (_.isArray(validRows) && validRows?.length > 0) {
        updateCPTYData(updateRows);
      }
    }
  };

  const generateFourDigitNumber = () => {
    return Math.floor(Math.random() * 10000) // 0–9999
      .toString()
      .padStart(4, "0"); // ensures length = 4
  };

  const onAddEvent = () => {
    setRowData((previousRows: any) => {
      const obj = {
        trade_blazer_account_no: generateFourDigitNumber(),
        trade_blazer_account_short_name: "",
        name: "",
        bucket_cb: "",
        bucket_estimated: "",
        probability_of_default_estimated: "",
        cb_rating: "",
        use_estimated: "",
        parent_account: "",
        credit_account: "",
        inherit_parent_account_pd: "",
        inherit_credit_account_pd: "",
        probability_of_default_cb: "",
        cc_allocation: "",
        isNewRowInsert: true,
        rowId: previousRows?.length + 1,
        hasError: false,
      };

      return [obj, ...previousRows];
    });
    setIsSaveDisabled(false);
    setIsCancelDisabled(false);
    setIsAddDisabled(true);
  };

  const onCellChange = (row: any, changeValue: any, column: Column) => {
    row.isRowUpdated = !row?.isNewRowInsert;
    row.updatedColumn = column.getColId();
    if (row.isRowUpdated) {
      setIsAddDisabled(true);
      setIsSaveDisabled(false);
      setIsCancelDisabled(false);
    }

    switch (column?.getColId()) {
      case "trade_blazer_account_short_name":
        row.isTBShortNameUpdate = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isTradeBlazerAccountShortNameValid =
          _.isNil(changeValue) || _.isEmpty(changeValue);
        break;
      case "bucket_cb":
        row.isBucketCBUpdate = true;

        // row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue)
        // row.isBucketCBValid = _.isNil(changeValue) || _.isEmpty(changeValue)

        break;
      case "name":
        row.isNameUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isNameValid = _.isNil(changeValue) || _.isEmpty(changeValue);
        break;
      case "bucket_estimated":
        row.isBucketEstimatedUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isBucketEstimatedValid =
          _.isNil(changeValue) || _.isEmpty(changeValue);
        break;
      case "probability_of_default_estimated":
        row.isPDEstimatedUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isPDEstimatedValid = _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "cb_rating":
        row.isCBRatingUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isCBRatingValid = _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "use_estimated":
        row.isUSEEstimatedUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isUseEstimatedValid =
          _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "parent_account":
        row.isPatentAccountUpdated = true;

        // row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue)
        // row.isParentAccountValid = _.isNil(changeValue) || _.isEmpty(changeValue)
        break;

      case "credit_account":
        row.isCreditAccountUpdated = true;

        // row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue)
        // row.isCreditAccountValid = _.isNil(changeValue) || _.isEmpty(changeValue)
        break;

      case "inherit_parent_account_pd":
        row.isInheritPatentAccPDUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isInheritParentAccountValid =
          _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "inherit_credit_account_pd":
        row.isInheritCreditAccPDUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isInheritCreditAccountValid =
          _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "probability_of_default_cb":
        row.isPDDefaultCBUpdated = true;
        row.hasError = _.isNil(changeValue) || _.isEmpty(changeValue);
        row.isPDCBValid = _.isNil(changeValue) || _.isEmpty(changeValue);
        break;

      case "cc_allocation":
        row.isCCAllocationUpdated = true;
        row.hasError = _.isNil(changeValue) ||  isNaN(changeValue);
        row.isCCAllocationValid =
          _.isNil(changeValue) || isNaN(changeValue);
        break;

      case "id":
        row.isIdUpdated = true;
        row.hasError = _.isNil(changeValue) || !isValidGuid(changeValue);
        row.isIdValid = _.isNil(changeValue) || isValidGuid(changeValue);
        break;

      default:
        break;
    }
    setRowData((previousRows: any) => {
      const data = [...previousRows];

      const ind = data?.findIndex((x: any) => x?.id === row?.id);

      if (ind >= 0) {
        data[ind] = row;
      }
      const errors = data?.filter((val: any) => val?.hasError);
      setHasError(errors?.length > 0);
      // setIsSaveDisabled(errors?.length > 0);

      return data;
    });
  };

  const onCancel = () => {
    getContractsByRisk();
  };

  const isValidRateRow = (row: any) => {
    const isValid =
      row?.trade_blazer_account_short_name &&
      !_.isEmpty(row?.trade_blazer_account_short_name) &&
      row?.name &&
      !_.isEmpty(row?.name) &&
      row?.cb_rating &&
      !_.isEmpty(row?.cb_rating) &&
      (VALID_RATINGS as readonly string[]).includes(
        row?.cb_rating?.toUpperCase()
      ) &&
      row?.use_estimated &&
      !_.isEmpty(row?.use_estimated) &&
      row?.id &&
      isValidGuid(row?.id) &&
       row?.inherit_credit_account_pd &&
      !_.isEmpty(row?.inherit_credit_account_pd) &&
      row?.inherit_parent_account_pd &&
      !_.isEmpty(row?.inherit_parent_account_pd)&&
       row?.probability_of_default_cb &&
      !isNaN(row?.probability_of_default_cb);

      
    // row?.bucket_estimated &&
    // !_.isEmpty(row?.bucket_estimated) &&
    // row?.probability_of_default_estimated &&
    // !_.isNaN(row?.probability_of_default_estimated) &&
    // row?.inherit_parent_account_pd &&
    // !_.isEmpty(row?.inherit_parent_account_pd) &&
    // row?.credit_account &&
    // !_.isEmpty(row?.credit_account) &&
    return isValid === true;
  };

  const onRefresh = () => {
    setIsSaveDisabled(true);
    setIsCancelDisabled(true);
    setIsAddDisabled(false);
    getContractsByRisk();
  };

  const handleClose = (
    event?: React.SyntheticEvent<Element, Event> | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <>
      <Snackbar open={open} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity="error"
          sx={{
            width: "100%",
            backgroundColor: "primary.main",
            textDecorationColor: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {doShowFullScreenLoader && <SpinnerFullScreen />}
      <div id="sec-master-breadcrumbs">
        <Breadcrumbs
          className="ms-1 "
          separator={<Icon icon="ic:baseline-greater-than" fontSize={20} />}
          aria-label="breadcrumb"
        ></Breadcrumbs>
        <div className="ps-3 mt-1" id="risk-title">
          CPTY Master
        </div>
        <div className="d-flex align-items-center justify-content-between px-2">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "0.75rem",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type="search"
                placeholder="Global search..."
                onInput={_.debounce(
                  (e: any) => gridApi?.setQuickFilter?.(e.target.value),
                  300
                )}
                style={{
                  height: "1.8rem",
                  width: "250px",
                  padding: "0 2.0rem 0 0.4rem",
                  borderRadius: "1rem",
                  border: "0.1px solid #cfd8e3",
                  background: "#fff",
                  boxShadow: "none",
                  fontSize: "0.85rem",
                  outline: "none",
                }}
              />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <button
              type="button"
              onClick={onAddEvent}
              className="metrics-btn p-2 h-65 d-flex align-items-center btn btn-primary me-1"
              style={{ height: "1.6rem" }}
              disabled={
                isAddDisabled ||
                !hasAccess([
                  `${pageConstant.RISK_BY_CPTY}_RW`,
                  `${pageConstant.RISK_BY_CPTY}_ADMIN`,
                ])
              }
            >
              Add
            </button>
            <button
              type="button"
              className="metrics-btn p-2 d-flex align-items-center btn btn-primary me-1"
              disabled={
                isSaveDisabled ||
                hasError ||
                !hasAccess([
                  `${pageConstant.RISK_BY_CPTY}_RW`,
                  `${pageConstant.RISK_BY_CPTY}_ADMIN`,
                ])
              }
              onClick={onSave}
              style={{ height: "1.6rem" }}
            >
              Save
            </button>
            <button
              type="button"
              className="metrics-btn p-2 d-flex align-items-center btn btn-primary me-1"
              onClick={onCancel}
              style={{ height: "1.6rem" }}
              disabled={
                isCancelDisabled ||
                !hasAccess([
                  `${pageConstant.RISK_BY_CPTY}_RW`,
                  `${pageConstant.RISK_BY_CPTY}_ADMIN`,
                ])
              }
            >
              Cancel
            </button>
            <img
              src={excel}
              alt="excel"
              onClick={() => {
                try {
                  const sheet =
                    gridRef?.current?.api?.getSheetDataForExcel?.({
                      sheetName: "Risk By Cpty",
                    }) ??
                    gridApi?.getSheetDataForExcel?.({
                      sheetName: "Risk By Cpty",
                    });
                  exportMultipleSheetsAsExcel({
                    data: [sheet],
                    fileName: "risk-by-cpty.xlsx",
                  });
                } catch (err) {
                  console.warn("Excel export failed", err);
                  setOpen(true);
                  setSnackbarMessage("Excel export failed");
                }
              }}
              style={{
                height: "1.7rem",
                width: "1.7rem",
                cursor: "pointer",
                marginRight: 3,
              }}
            />
            <div
              className="d-flex align-items-center cursor-pointer"
              
            >
              <RefreshIcon
              onClick={() => {
                onRefresh();
                setLastUpdatedDate(moment(new Date()).format(constants.RefreshFormat));
                setTimeout(() => gridApi?.sizeColumnsToFit?.(), 150);
              }}
             
                style={{
                  cursor: "pointer",
                  marginRight: "0.5rem",
                  color: "#1E355E",
                }}
              />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "0.72rem" }}>Last updated on:</div>
                <div style={{ fontSize: "0.78rem" }}>{lastUpdatedDate}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Card style={{ height: getCardHeight() }} className="m-1">
        <CardContent className="h-100 w-100 d-flex flex-row px-2 pt-1 pb-2">
          <RiskByCptyGrid
            rowData={rowData}
            getGridRef={(ref: any) => {
              setGridRef(ref);
            }}
            getGridApi={(api: any, colApi: any) => {
              setGridApi(api);
              setGridColumnApi(colApi);
              setTimeout(() => api?.sizeColumnsToFit?.(), 120);
            }}
            refresh={() => {
              onRefresh();
              setTimeout(() => gridApi?.sizeColumnsToFit?.(), 200);
            }}
            onSave={onSave}
            onCancel={onCancel}
            onCellChange={(data: any, value: any, column: Column) => {
              onCellChange(data, value, column);
            }}
            onAddEvent={onAddEvent}
            isSaveDisabled={isSaveDisabled || hasError}
            isAddDisabled={isAddDisabled}
            isCancelDisabled={isCancelDisabled}
          />
        </CardContent>
      </Card>
    </>
  );
};
RiskByCpty.page = pageConstant.RISK_BY_CPTY;
export default RiskByCpty;
